import { useContext } from 'react';
import axios from 'axios';

import getAccessToken from '../auth/getAccessToken';
import getRefreshToken from '../auth/getRefreshToken';
import { UserContext } from '../globalContext/user/userProvider';
import { getItem } from '../services/localStorageService';
import { LOCAL_STORAGE_KEYS, STATUS_CODES } from '../consts/constants';
import { API_VERSION, API_MODULE } from '../consts/endpoint';
import { getBrandApiHeaders } from '../hooks/useBrandTheme';
import { redirectToLogin } from '../utils/logoutRedirect';

interface AxiosParamsInterface {
  apiVersion?: string;
  omitToken?: boolean;
  apiModule?: string;
  apiBaseUrl?: string;
}

const useAxiosInstance = (params?: AxiosParamsInterface) => {
  const {
    apiVersion = API_VERSION.V1,
    omitToken,
    apiModule = API_MODULE.API,
  } = params || {};
  axios.defaults.headers.common['Content-Type'] = 'application/json';

  const getBaseUrl = () => {
  if (params?.apiBaseUrl) {
    return params.apiBaseUrl.endsWith('/') 
      ? `${params.apiBaseUrl}${apiVersion}`
      : `${params.apiBaseUrl}/${apiVersion}`;
  }
  
  if (!apiModule || apiModule === API_MODULE.API) {
    return `${process.env.REACT_APP_API_URL || 'https://api.sparkl.ac/api/'}${apiVersion}`;
  }
  
  return `${process.env.REACT_APP_API_URL?.replace('/api/', '/') || 'https://api.sparkl.ac/'}${apiModule}/${apiVersion}`;
};

const baseURL = getBaseUrl();

  const axiosInstance = axios.create({
    baseURL,
  });

  const [userInfo] = useContext(UserContext);

  axiosInstance.interceptors.request.use((request) => {
    // Read userId and userType from localStorage on each request to ensure fresh values
    const userId = getItem(LOCAL_STORAGE_KEYS.USER_ID);
    const userType = getItem(LOCAL_STORAGE_KEYS.USER_TYPE);
    
    if (!omitToken) {
      const accessToken = getAccessToken() || null;
      const refreshToken = getRefreshToken() || null;
      
      // Token header = refresh token
      if (refreshToken) {
        request.headers.token = refreshToken;
      }
      
      // X-Refresh-Token header = refresh token
      if (refreshToken) {
        request.headers['X-Refresh-Token'] = refreshToken;
      }
      
      // X-Access-Token header = Bearer access token (for all APIs)
      if (accessToken) {
        request.headers['X-Access-Token'] = `Bearer ${accessToken}`;
      }
    }
    
    const finalUserType = userInfo?.userType || userType;
    const finalUserId = userInfo?.id || userId;
    
    if (finalUserType && finalUserId) {
      const brandHeaders = getBrandApiHeaders();
      request.headers['user-type'] = finalUserType;
      request.headers['user-id'] = finalUserId;
      request.headers['X-BU-Id'] = brandHeaders['X-BU-Id'];
      request.headers['X-App-Id'] = brandHeaders['X-App-Id'];
      request.headers['X-User-Type'] = finalUserType;
      request.headers['X-User-Id'] = finalUserId;
    }

    return request;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      if (
        !omitToken &&
        (response.status === STATUS_CODES.UNAUTHORIZED_USER ||
          response?.status === STATUS_CODES.TOO_MANY_REQUESTS)
      ) {
        redirectToLogin();
        return response;
      }
      return response;
    },
    (error) => {
      if (
        !omitToken &&
        (error.response?.status === STATUS_CODES.UNAUTHORIZED_USER ||
          error.response?.status === STATUS_CODES.TOO_MANY_REQUESTS)
      ) {
        redirectToLogin();
        return Promise.reject(error);
      }
      return Promise.reject(error);
    },
  );

  return { axiosInstance };
};

export default useAxiosInstance;
